import type {
  CommentQueryParams,
  CommentsResponse,
  EventResponse,
  MarketActivityQueryParams,
  MarketActivityResponse,
  MarketHoldersQueryParams,
  MarketHoldersResponse,
  PricesHistoryQueryParams,
  PricesHistoryResponse,
} from '@/types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS, MESSAGES } from '@/constants';

export interface GrokSummaryResponse {
  success: boolean;
  data: string;
  meta: {
    prompt: string;
    locale: string;
    timestamp: string;
  };
}

export const marketDetailsService = {
  getGrokSummary: async (prompt: string, locale: string = 'en'): Promise<GrokSummaryResponse> => {
    const response = await axiosClient.post<GrokSummaryResponse>(
      `${API_ENDPOINTS.MARKETS_GROK_SUMMARY}?prompt=${prompt}&locale=${locale}`
    );
    return response.data;
  },

  getById: async (id: string): Promise<EventResponse> => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.MARKETS_EVENT_BY_SLUG(id));

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }
    } catch (error) {
      console.error(`Failed to fetch market/event for id "${id}"`, error);
    }

    throw new Error(MESSAGES.API.EVENT_NOT_FOUND(id));
  },
  getCommentsPaginated: async (params: CommentQueryParams): Promise<CommentsResponse> => {
    const response = await axiosClient.get<CommentsResponse>(API_ENDPOINTS.MARKETS_COMMENTS, {
      params,
    });
    return response.data;
  },
  getHolders: async (params: MarketHoldersQueryParams): Promise<MarketHoldersResponse> => {
    const response = await axiosClient.get<MarketHoldersResponse>(API_ENDPOINTS.MARKETS_HOLDERS, {
      params,
    });
    return response.data;
  },
  getActivity: async (params: MarketActivityQueryParams): Promise<MarketActivityResponse> => {
    const response = await axiosClient.get<MarketActivityResponse>(API_ENDPOINTS.MARKETS_ACTIVITY, {
      params,
    });
    return response.data;
  },
  getPricesHistory: async (params: PricesHistoryQueryParams): Promise<PricesHistoryResponse> => {
    const response = await axiosClient.get<PricesHistoryResponse>(
      API_ENDPOINTS.MARKETS_PRICES_HISTORY,
      {
        params: {
          market: params.market,
          interval: params.interval,
          fidelity: params.fidelity || 60,
        },
      }
    );
    return response.data;
  },
};
